/**
 * A NIK's digits 7 to 12 are the birth date as DDMMYY, the day plus 40 for a woman (the warning
 * under every typed NIK field, apps/web isian/nomor `peringatanNikLahir`). A partial birth date
 * is compared as far as it goes. Pure; the result names a mismatch, never the NIK itself.
 */
import { parsePartialDate, parsePartialDateInput } from './dates/partial'

export type HasilNik = 'cocok' | 'tanggal' | 'jenis_kelamin' | 'tidak_bisa'

/**
 * A 16-digit NIK against a birth date (canonical `1987-05-12` or typed `12-05-1987`, `1987`
 * or `05-1987`) and sex. `tidak_bisa` when there is nothing to compare: not 16 digits, or no
 * readable birth date and no sex that contradicts. Date digits no one is born on (day 0, 32
 * to 40, above 71; month 0 or above 12) say nothing about sex and never match a date.
 */
export function cekNikLahir(
  nik: string,
  tanggalLahir: string | null,
  jenisKelamin: 'L' | 'P' | null,
): HasilNik {
  if (!/^\d{16}$/.test(nik)) return 'tidak_bisa'
  const dd = Number(nik.slice(6, 8))
  const mm = Number(nik.slice(8, 10))
  const yy = nik.slice(10, 12)
  const hari = dd > 40 ? dd - 40 : dd
  const mungkin = hari >= 1 && hari <= 31 && mm >= 1 && mm <= 12
  if (mungkin && jenisKelamin !== null && (dd > 40 ? 'P' : 'L') !== jenisKelamin) {
    return 'jenis_kelamin'
  }
  const kanonik = tanggalLahir === null ? null : parsePartialDateInput(tanggalLahir)
  const p = kanonik === null ? null : parsePartialDate(kanonik)
  if (p === null) return 'tidak_bisa'
  if (!mungkin) return 'tanggal'
  if (String(p.tahun % 100).padStart(2, '0') !== yy) return 'tanggal'
  if (p.bulan !== undefined && p.bulan !== mm) return 'tanggal'
  if (p.hari !== undefined && p.hari !== hari) return 'tanggal'
  return 'cocok'
}
